import { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { FaTimes } from 'react-icons/fa';
import TripBookingStatusFilter from './TripBookingStatusFilter';
import BookedByFilter from './BookedByFilter';
import TripDayFilter from './TripDayFilter/TripDayFilter';
import { searchTripsReset } from '../../../../store/slices/trip';
import CruisePaymentFilter from './CruisePaymentFilter/CruisePaymentFilter';
import CustomerBookedTripFilter from './CustomerBookedTripFilter';
import { RangeYearsForSearching, SearchModes } from '../../../../constants';
import OrganizationsFilter from './OrganizationFilter';
import checkIsAdmin from '../../../../helpers/selectors/checkIsAdmin';
import CustomBookingFieldsFilter from './CustomBookingFieldsFilter';
import TravelAgencyFilter from './TravelAgencyFilter';

const TripsFilters = ({ onSearch }) => {
  const dispatch = useDispatch();
  // const [isExpanded, setIsExpanded] = useState(false);
  const { tripsSearch } = useSelector((store) => store.trip);
  const { tripStartDateFrom, tripStartDateTo, toTripBookingDate, fromTripBookingDate } =
    tripsSearch.searchObj;
  const { areFiltersEmpty } = useSelector((store) => store.trip.tripsSearch.searchObj);
  const { user } = useSelector((store) => store);

  const doShowCruiseDepositDueAndFinalPayment =
    user &&
    user.travelAgency &&
    user.travelAgency.enabledTransportModes &&
    user.travelAgency.enabledTransportModes.includes(+SearchModes.CRUISE_TRIP);

  const isAdminUser = useSelector(checkIsAdmin);
  const DateNow = new Date();
  const minDate = new Date(
    new Date().setFullYear(DateNow.getFullYear() - RangeYearsForSearching.AGO_YEARS),
  );

  const maxDateTripFrom = tripStartDateTo
    ? new Date(tripStartDateTo)
    : new Date(new Date().setFullYear(DateNow.getFullYear() + RangeYearsForSearching.AHEAD_YEARS));
  const coefTripFrom = tripStartDateFrom ? 1 : 2;
  const minDateTripTo = tripStartDateFrom ? new Date(tripStartDateFrom) : minDate;
  const maxDateTripTo = new Date(
    new Date().setFullYear(
      minDateTripTo.getFullYear() + coefTripFrom * RangeYearsForSearching.AHEAD_YEARS,
    ),
  );

  const maxDateBookingFrom = toTripBookingDate
    ? new Date(toTripBookingDate)
    : new Date(new Date().setFullYear(DateNow.getFullYear() + RangeYearsForSearching.AHEAD_YEARS));
  const coefBookingFrom = fromTripBookingDate ? 1 : 2;
  const minDateBookingTo = fromTripBookingDate ? new Date(fromTripBookingDate) : minDate;
  const maxDateBookingTo = new Date(
    new Date().setFullYear(
      minDateBookingTo.getFullYear() + coefBookingFrom * RangeYearsForSearching.AGO_YEARS,
    ),
  );

  const handleDayFilterChange = useCallback(
    (value, fieldName) => {
      const searchObj = {
        ...tripsSearch.searchObj,
        page: 1,
      };
      _.set(searchObj, fieldName, value);
      onSearch(searchObj);
    },
    [onSearch, tripsSearch.searchObj],
  );

  const handleClearFilters = () => {
    dispatch(searchTripsReset());
    onSearch({
      page: 1,
    });
  };

  return (
    <div className="mb-6 text-theme">
      <div className="flex sm:flex-nowrap flex-wrap-reverse">
        <div className="flex flex-wrap justify-start gap-2 mb-2">
          <BookedByFilter onSearch={onSearch} />
          <TripBookingStatusFilter onSearch={onSearch} />
          <CustomBookingFieldsFilter onSearch={onSearch} />
          {doShowCruiseDepositDueAndFinalPayment && (
            <CruisePaymentFilter
              onSearch={onSearch}
              label="Cruise deposit due"
              labelSuggest="Show cruise bookings having deposit due"
              selectedOption="depositDue"
            />
          )}
          {doShowCruiseDepositDueAndFinalPayment && (
            <CruisePaymentFilter
              onSearch={onSearch}
              label="Cruise final payment due"
              labelSuggest="Show cruise bookings having final payment due"
              selectedOption="finalPaymentDue"
            />
          )}
          <TripDayFilter
            value={tripsSearch.searchObj.tripStartDateFrom}
            onChange={(selectedDay) => handleDayFilterChange(selectedDay, 'tripStartDateFrom')}
            inputProps={{ placeholder: 'Trip start date (from)' }}
            ablePast
            minDate={minDate}
            maxDate={maxDateTripFrom}
          />
          <TripDayFilter
            value={tripsSearch.searchObj.tripStartDateTo}
            onChange={(selectedDay) => handleDayFilterChange(selectedDay, 'tripStartDateTo')}
            inputProps={{ placeholder: 'Trip start date (to)' }}
            ablePast
            minDate={minDateTripTo}
            maxDate={maxDateTripTo}
          />
          <TripDayFilter
            value={tripsSearch.searchObj.fromTripBookingDate}
            onChange={(selectedDay) => handleDayFilterChange(selectedDay, 'fromTripBookingDate')}
            inputProps={{ placeholder: 'Booking date (from)' }}
            ablePast
            minDate={minDate}
            maxDate={maxDateBookingFrom}
          />
          <TripDayFilter
            value={tripsSearch.searchObj.toTripBookingDate}
            onChange={(selectedDay) => handleDayFilterChange(selectedDay, 'toTripBookingDate')}
            inputProps={{ placeholder: 'Booking date (to)' }}
            ablePast
            minDate={minDateBookingTo}
            maxDate={maxDateBookingTo}
          />
          <CustomerBookedTripFilter onSearch={onSearch} />
          {isAdminUser && <OrganizationsFilter onSearch={onSearch} />}
          {isAdminUser && <TravelAgencyFilter onSearch={onSearch} />}
        </div>
        <div>
          {/* <button
            className="py-3 px-4 font-medium focus:outline-none max-w-1/8"
            type="button"
            onClick={() => setIsExpanded((v) => !v)}
          >
            {isExpanded ? 'Less filters' : 'More filters'}
            {' '}
            <FaChevronDown className="inline-block ml-1 h-3" />
          </button> */}

          {!areFiltersEmpty && (
            <button
              className="py-3 px-4 font-medium focus:outline-none max-w-1/8 whitespace-nowrap"
              type="button"
              onClick={handleClearFilters}
            >
              Clear filters <FaTimes className="inline-block ml-1 h-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

TripsFilters.propTypes = {
  onSearch: PropTypes.func.isRequired,
};

export default memo(TripsFilters);
